import {
  Component,
  computed,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicI18nService, AggregatedSlot, BookingRequest, BookingRequestResponse } from '@frontend/public-calendar';
import { DEFAULT_TIMEZONE } from '@frontend/util';
import {
  SectionTheme,
  LegalPageConfig,
  DEFAULT_SECTION_THEME,
  SECTION_THEME,
} from '../section-renderers/section-renderer.models';
import { HeroRendererComponent } from '../section-renderers/hero-renderer.component';
import { HeaderRendererComponent } from '../section-renderers/header-renderer.component';
import { FooterRendererComponent, LegalPageNavigationEvent } from '../section-renderers/footer-renderer.component';
import { TeamRendererComponent } from '../section-renderers/team-renderer.component';
import { StatsRendererComponent } from '../section-renderers/stats-renderer.component';
import { FeaturesRendererComponent } from '../section-renderers/features-renderer.component';
import { BlogRendererComponent } from '../section-renderers/blog-renderer.component';
import { CalendarRendererComponent, WeekChangeEvent } from '../section-renderers/calendar-renderer.component';

/**
 * Factory function for creating the theme signal.
 * Used in providers to create a fresh signal per component instance.
 */
function themeSignalFactory() {
  return signal<SectionTheme>(DEFAULT_SECTION_THEME);
}

/**
 * Section data passed to the full-width preview.
 * When contentDe and contentEn are provided, the component resolves
 * the appropriate content based on the current locale.
 */
export interface FullwidthSection {
  id: string;
  type: string;
  name: string;
  displayOrder: number;
  settings: Record<string, unknown>;
  /** Pre-resolved content (used when contentDe/contentEn not provided) */
  content: Record<string, unknown>;
  /** German content - when provided, enables reactive language switching */
  contentDe?: Record<string, unknown>;
  /** English content - when provided, enables reactive language switching */
  contentEn?: Record<string, unknown>;
}

/**
 * Full-width preview component for the WordPress-like page builder.
 * Renders sections using full-width Tailwind-styled renderers.
 *
 * Provides SECTION_THEME via DI so all child renderers can inject it
 * for reactive theme updates without prop drilling.
 */
@Component({
  selector: 'dcp-fullwidth-preview',
  standalone: true,
  imports: [
    CommonModule,
    HeroRendererComponent,
    HeaderRendererComponent,
    FooterRendererComponent,
    TeamRendererComponent,
    StatsRendererComponent,
    FeaturesRendererComponent,
    BlogRendererComponent,
    CalendarRendererComponent,
  ],
  providers: [
    { provide: SECTION_THEME, useFactory: themeSignalFactory },
  ],
  template: `
    <div
      class="min-h-full"
      [style.background-color]="themeSignal().backgroundColor"
      [style.color]="themeSignal().textColor"
      [style.font-family]="themeSignal().fontFamily"
      [attr.style]="backgroundPatternStyle()"
    >
      <!-- Render sections in order -->
      @for (section of orderedSections(); track section.id) {
        <div
          class="section-wrapper scroll-mt-16"
          [class.sticky]="section.type === 'Header' && section.settings['sticky']"
          [class.top-0]="section.type === 'Header' && section.settings['sticky']"
          [class.z-50]="section.type === 'Header' && section.settings['sticky']"
          [class.cursor-pointer]="previewMode"
          [id]="'section-' + slugify(section.name)"
          (click)="previewMode && onSectionClick($event, section.id)"
          (keydown.enter)="previewMode && onSectionClick($event, section.id)"
          [attr.data-section-id]="section.id"
          [attr.tabindex]="previewMode ? 0 : null"
          [attr.role]="previewMode ? 'button' : null"
          [attr.aria-label]="previewMode ? 'Select ' + section.type + ' section' : null"
        >
          @switch (section.type) {
            @case ('Header') {
              <dcp-header-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
              />
            }
            @case ('Hero') {
              <dcp-hero-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
                [locale]="currentLanguage()"
              />
            }
            @case ('Stats') {
              <dcp-stats-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
              />
            }
            @case ('Team') {
              <dcp-team-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
              />
            }
            @case ('Features') {
              <dcp-features-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
              />
            }
            @case ('Blog') {
              <dcp-blog-renderer
                [settingsInput]="section.settings"
                [contentInput]="section.content"
              />
            }
            @case ('Calendar') {
              @defer (on viewport) {
                <dcp-calendar-renderer
                  [settingsInput]="section.settings"
                  [contentInput]="section.content"
                  [slots]="calendarSlots()"
                  [loadingInput]="calendarLoading()"
                  [previewModeInput]="previewMode"
                  [timezoneInput]="calendarTimezone()"
                  [submittingInput]="calendarSubmitting()"
                  [formErrorInput]="calendarFormError()"
                  [showSuccessInput]="calendarShowSuccess()"
                  [successResponseInput]="calendarSuccessResponse()"
                  (weekChanged)="onCalendarWeekChanged($event)"
                  (submitRequest)="onCalendarSubmitRequest($event)"
                  (successDismissed)="onCalendarSuccessDismissed()"
                />
              } @placeholder {
                <div class="py-16 flex items-center justify-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2" [style.border-color]="themeSignal().primaryColor"></div>
                </div>
              } @loading (minimum 200ms) {
                <div class="py-16 flex items-center justify-center">
                  <div class="animate-spin rounded-full h-8 w-8 border-b-2" [style.border-color]="themeSignal().primaryColor"></div>
                </div>
              }
            }
            @case ('TextBlock') {
            <!-- TextBlock section -->
            <section
              class="relative"
              [class]="sectionSpacingClass()"
              [style.background-color]="section.settings['backgroundColor'] || themeSignal().backgroundColor"
            >
              <div class="mx-auto max-w-3xl px-6 lg:px-8">
                @if (section.content['title']) {
                  <h2 class="text-2xl font-bold mb-4" [style.color]="themeSignal().textColor">
                    {{ section.content['title'] }}
                  </h2>
                }
                @if (section.content['html'] || section.content['content']) {
                  <div
                    class="prose prose-lg max-w-none"
                    [style.color]="themeSignal().textColor"
                    [innerHTML]="section.content['html'] || section.content['content']"
                  ></div>
                }
                @if (!section.content['title'] && !section.content['html'] && !section.content['content']) {
                  <div class="text-center py-8">
                    <p class="text-sm" [style.color]="themeSignal().textMutedColor">
                      Text block - add content in the editor
                    </p>
                  </div>
                }
              </div>
            </section>
            }
            @case ('Footer') {
            <dcp-footer-renderer
              [settingsInput]="section.settings"
              [contentInput]="section.content"
              [legalPageConfig]="legalPageConfig"
              [previewMode]="previewMode"
              [locale]="currentLanguage()"
              (legalPageNavigation)="legalPageNavigation.emit($event)"
            />
            }
            @default {
            <!-- Unknown section type placeholder -->
            <section
              class="relative"
              [class]="sectionSpacingClass()"
              [style.background-color]="themeSignal().surfaceColor"
            >
              <div class="mx-auto px-6 lg:px-8" [class]="containerWidthClass()">
                <div class="text-center">
                  <div
                    class="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
                    [style.background-color]="themeSignal().primaryColor + '20'"
                  >
                    <svg class="w-6 h-6" [style.color]="themeSignal().primaryColor" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
                    </svg>
                  </div>
                  <p class="text-lg font-medium" [style.color]="themeSignal().textColor">
                    {{ section.type }} Section
                  </p>
                  <p class="mt-1 text-sm" [style.color]="themeSignal().textMutedColor">
                    Full-width renderer coming soon
                  </p>
                </div>
              </div>
            </section>
            }
          }
        </div>
      }

      <!-- Empty state when no sections -->
      @if (orderedSections().length === 0) {
        <div class="min-h-[60vh] flex items-center justify-center">
          <div class="text-center">
            <svg class="mx-auto h-16 w-16 opacity-30" fill="none" viewBox="0 0 24 24" stroke-width="1" [style.stroke]="themeSignal().textMutedColor">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
            <h3 class="mt-4 text-lg font-medium" [style.color]="themeSignal().textColor">
              No sections yet
            </h3>
            <p class="mt-2 text-sm" [style.color]="themeSignal().textMutedColor">
              Add sections in the page editor to see them here
            </p>
          </div>
        </div>
      }
    </div>
  `,
  styles: [`
    :host {
      display: block;
      height: 100%;
    }
  `],
})
export class FullwidthPreviewComponent {
  /** Injected theme signal - provided by this component, shared with all child renderers */
  readonly themeSignal = inject(SECTION_THEME);

  /** I18n service for reactive locale-based content resolution */
  private readonly i18n = inject(PublicI18nService);

  private readonly _sections = signal<FullwidthSection[]>([]);
  private readonly _calendarTitle = signal<string>('Book an Appointment');
  private readonly _language = signal<'de' | 'en'>('de');
  private readonly _calendarName = signal<string>('');
  private readonly _calendarSlots = signal<AggregatedSlot[]>([]);
  private readonly _calendarLoading = signal<boolean>(false);
  private readonly _calendarSubmitting = signal<boolean>(false);
  private readonly _calendarFormError = signal<string | null>(null);
  private readonly _calendarShowSuccess = signal<boolean>(false);
  private readonly _calendarSuccessResponse = signal<BookingRequestResponse | null>(null);
  private readonly _calendarTimezone = signal<string>(DEFAULT_TIMEZONE);

  /** Emits section ID when a section is clicked (for click-to-select in editor) */
  @Output() sectionClicked = new EventEmitter<string>();

  /** Emits when a legal page link is clicked in preview mode */
  @Output() legalPageNavigation = new EventEmitter<LegalPageNavigationEvent>();

  /** Emits when the calendar week changes (for fetching slots) */
  @Output() weekChanged = new EventEmitter<WeekChangeEvent>();

  /** Emits when a booking request is submitted */
  @Output() submitRequest = new EventEmitter<BookingRequest>();

  /** Emits when the success modal is dismissed */
  @Output() successDismissed = new EventEmitter<void>();

  @Input() set sections(value: FullwidthSection[]) {
    this._sections.set(value || []);
  }

  @Input() set calendarTitleInput(value: string) {
    this._calendarTitle.set(value || 'Book an Appointment');
  }

  @Input() set language(value: 'de' | 'en') {
    this._language.set(value || 'de');
  }

  @Input() set calendarName(value: string) {
    this._calendarName.set(value || '');
  }

  /** Slots for the calendar section (from API) */
  @Input() set calendarSlotsInput(value: AggregatedSlot[]) {
    this._calendarSlots.set(value || []);
  }

  /** Whether calendar slots are loading */
  @Input() set calendarLoadingInput(value: boolean) {
    this._calendarLoading.set(value);
  }

  /** Whether booking form is submitting */
  @Input() set calendarSubmittingInput(value: boolean) {
    this._calendarSubmitting.set(value);
  }

  /** Calendar form error message */
  @Input() set calendarFormErrorInput(value: string | null) {
    this._calendarFormError.set(value);
  }

  /** Whether to show success modal */
  @Input() set calendarShowSuccessInput(value: boolean) {
    this._calendarShowSuccess.set(value);
  }

  /** Success response data */
  @Input() set calendarSuccessResponseInput(value: BookingRequestResponse | null) {
    this._calendarSuccessResponse.set(value);
  }

  /** Timezone for calendar section (from API) */
  @Input() set calendarTimezoneInput(value: string) {
    this._calendarTimezone.set(value || DEFAULT_TIMEZONE);
  }

  /** Legal page configuration for footer auto-links */
  @Input() legalPageConfig?: LegalPageConfig;

  /** Preview mode - disables link navigation in footer */
  @Input() previewMode = false;

  /** Computed accessors for calendar state */
  readonly calendarSlots = computed(() => this._calendarSlots());
  readonly calendarLoading = computed(() => this._calendarLoading());
  readonly calendarSubmitting = computed(() => this._calendarSubmitting());
  readonly calendarFormError = computed(() => this._calendarFormError());
  readonly calendarShowSuccess = computed(() => this._calendarShowSuccess());
  readonly calendarSuccessResponse = computed(() => this._calendarSuccessResponse());
  readonly calendarTimezone = computed(() => this._calendarTimezone());

  /** Theme input - updates the injected signal for reactivity */
  @Input() set theme(value: SectionTheme) {
    if (value) {
      this.themeSignal.set(value);
    }
  }

  /** Container width CSS class based on theme */
  readonly containerWidthClass = computed(() => {
    const width = this.themeSignal().containerWidth || 'default';
    const map: Record<string, string> = {
      narrow: 'max-w-4xl',
      default: 'max-w-7xl',
      wide: 'max-w-[90rem]',
      full: 'max-w-full',
    };
    return map[width] || 'max-w-7xl';
  });

  /** Section spacing CSS classes based on theme */
  readonly sectionSpacingClass = computed(() => {
    const spacing = this.themeSignal().sectionSpacing || 'default';
    const map: Record<string, string> = {
      compact: 'py-8 sm:py-12',
      default: 'py-16 sm:py-24',
      relaxed: 'py-24 sm:py-32',
    };
    return map[spacing] || 'py-16 sm:py-24';
  });

  /** Background pattern CSS/style based on theme */
  readonly backgroundPatternStyle = computed(() => {
    const pattern = this.themeSignal().backgroundPattern || 'none';
    if (pattern === 'none') return '';
    // Pattern styles - subtle background patterns
    const patterns: Record<string, string> = {
      dots: 'background-image: radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px); background-size: 20px 20px;',
      grid: 'background-image: linear-gradient(rgba(0,0,0,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.03) 1px, transparent 1px); background-size: 20px 20px;',
      waves: 'background-image: url("data:image/svg+xml,%3Csvg width=\'100\' height=\'20\' viewBox=\'0 0 100 20\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M21.184 20c.357-.13.72-.264 1.088-.402l1.768-.661C33.64 15.347 39.647 14 50 14c10.271 0 15.362 1.222 24.629 4.928.955.383 1.869.74 2.75 1.072h6.225c-2.51-.73-5.139-1.691-8.233-2.928C65.888 13.278 60.562 12 50 12c-10.626 0-16.855 1.397-26.66 5.063l-1.767.662c-2.475.923-4.66 1.674-6.724 2.275h6.335zm0-20C13.258 2.892 8.077 4 0 4V2c5.744 0 9.951-.574 14.85-2h6.334zM77.38 0C85.239 2.966 90.502 4 100 4V2c-6.842 0-11.386-.542-16.396-2h-6.225zM0 14c8.44 0 13.718-1.21 22.272-4.402l1.768-.661C33.64 5.347 39.647 4 50 4c10.271 0 15.362 1.222 24.629 4.928C84.112 12.722 89.438 14 100 14v-2c-10.271 0-15.362-1.222-24.629-4.928C65.888 3.278 60.562 2 50 2 39.374 2 33.145 3.397 23.34 7.063l-1.767.662C13.223 10.84 8.163 12 0 12v2z\' fill=\'rgba(0,0,0,0.03)\' fill-rule=\'evenodd\'/%3E%3C/svg%3E");',
      gradient: 'background: linear-gradient(135deg, rgba(0,0,0,0.02) 0%, transparent 50%, rgba(0,0,0,0.02) 100%);',
      mesh: 'background-image: radial-gradient(at 40% 20%, rgba(99,102,241,0.05) 0px, transparent 50%), radial-gradient(at 80% 0%, rgba(56,189,248,0.05) 0px, transparent 50%), radial-gradient(at 0% 50%, rgba(232,121,249,0.05) 0px, transparent 50%);',
      noise: 'background-image: url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.65\' numOctaves=\'3\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' opacity=\'0.03\'/%3E%3C/svg%3E");',
    };
    return patterns[pattern] || '';
  });

  /**
   * Ordered sections with locale-resolved content.
   * When sections have contentDe/contentEn, resolves based on current locale.
   * This enables reactive language switching for section content.
   */
  readonly orderedSections = computed(() => {
    // Read locale to establish signal dependency for reactive updates
    const locale = this.i18n.locale();

    return [...this._sections()]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map(section => {
        // If section has both contentDe and contentEn, resolve based on locale
        if (section.contentDe && section.contentEn) {
          return {
            ...section,
            content: locale === 'en' ? section.contentEn : section.contentDe,
          };
        }
        // Otherwise use pre-resolved content
        return section;
      });
  });

  readonly calendarTitle = computed(() => this._calendarTitle());

  /**
   * Current language for child renderers.
   * Uses PublicI18nService.locale() for reactive updates when user clicks language button.
   */
  readonly currentLanguage = computed(() => this.i18n.locale());

  readonly displayCalendarName = computed(() => this._calendarName());

  /**
   * Intercept clicks on anchor links (href="#...") to enable smooth scrolling.
   * This handles links inside section renderers (CTA buttons, hero buttons, header nav).
   * Supports both old format (#calendar) and new format (#section-calendar).
   */
  @HostListener('click', ['$event'])
  onAnchorClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');

    if (!anchor) return;

    const href = anchor.getAttribute('href');
    if (!href || !href.startsWith('#')) return;

    // Prevent default navigation for ALL anchor links
    event.preventDefault();
    event.stopPropagation();

    // Handle "#" (scroll to top)
    if (href === '#') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Find target element by ID
    const targetId = href.substring(1); // Remove the #
    let targetElement = document.getElementById(targetId);

    // Fallback: if not found and doesn't have section- prefix, try with prefix
    if (!targetElement && !targetId.startsWith('section-')) {
      targetElement = document.getElementById(`section-${targetId}`);
    }

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /** Handle section click for selection - but not if clicking an anchor link */
  onSectionClick(event: Event, sectionId: string): void {
    // Don't emit section click if the user clicked an anchor link
    const target = event.target as HTMLElement;
    if (target.closest('a')) {
      return; // Let the anchor click handler deal with this
    }

    event.stopPropagation();
    this.sectionClicked.emit(sectionId);
  }

  /** Handle calendar week change */
  onCalendarWeekChanged(range: WeekChangeEvent): void {
    this.weekChanged.emit(range);
  }

  /** Handle calendar booking submission */
  onCalendarSubmitRequest(request: BookingRequest): void {
    this.submitRequest.emit(request);
  }

  /** Handle calendar success modal dismissal */
  onCalendarSuccessDismissed(): void {
    this.successDismissed.emit();
  }

  /**
   * Converts a section name to a URL-safe slug for anchor links.
   * E.g., "Hero 2" → "hero-2", "My CTA Section" → "my-cta-section"
   */
  slugify(name: string): string {
    return (name || '')
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  }
}
